import './EsErrorPage.css';
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { useEsStrings } from '../../common/EsStrings';
import { EsChunkUpdated, isChunkLoadError } from '../../common/EsLazy';

/*
 * The router's error boundary -- index.js hangs it off the root route.
 *
 * TWO SHAPES ARRIVE HERE AND THIS COMPONENT ONLY EVER READ ONE OF EACH.
 * useRouteError hands back whatever was thrown, and react-router throws two
 * different things:
 *
 *   - an ErrorResponse -- { status, statusText, data } -- for a routing
 *     refusal, which is what an unmatched url is. It has NO `message`.
 *   - a plain Error, for a component that threw while rendering. It has
 *     `message` and `name`, and NO `statusText`.
 *
 * The page was printing error.statusText and error.message side by side, so
 * whichever shape turned up, one of the two paragraphs rendered empty -- and an
 * empty <p> has no height, which is why the 404 scenario could not find
 * es-error-message at all. A 404 showing a heading over nothing is the exact
 * failure the page exists to prevent: it is indistinguishable from the bundle
 * not loading.
 *
 * `data` is where the router puts its own explanation of a routing refusal
 * ("Error: No route matches URL ..."), so it is the message in that case, and
 * `name` is the nearest thing a thrown Error has to a status line.
 */

/**
 * The status line: the response's reason phrase, or the error's class name.
 *
 * <p>No error at all means this is the catch-all route in index.js rather than
 * an error boundary, and the only thing that reaches that route is a url the
 * app does not have. The component says so itself, in copy, rather than two
 * blank lines under a heading that announces an error and then does not name one.
 */
function esErrorStatus( pError ) {
    if ( isRouteErrorResponse( pError ) ) return pError.statusText;
    return pError.name ?? "";
}

/**
 * The explanation. A router refusal carries it in `data`, which is a string
 * for every refusal the router raises itself -- but a loader throwing json()
 * can put an object there, and React refuses to render one, so anything that
 * is not already text is stringified rather than blanking the page it is
 * supposed to be explaining.
 */
function esErrorMessage( pError ) {
    if ( isRouteErrorResponse( pError ) ) {
        return typeof pError.data === "string" ? pError.data : JSON.stringify( pError.data );
    }
    return pError.message ?? "";
}

export default function EsErrorPage() {
    const t = useEsStrings();
    const error = useRouteError();
    // A chunk a deploy took, from a lazy page outside esLazy's boundary: say so, not "ChunkLoadError".
    if ( isChunkLoadError( error ) ) return <EsChunkUpdated />;
    return (
        <section data-testid="es-error-page" className="relative z-10 pb-4 pt-nav-room">
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]">

                            <p data-testid="es-error-heading" className="errorPageHeading">
                                { t( 'common-error-heading', 'An Error Occurred' ) }
                            </p>
                            <p data-testid="es-error-status" className="errorPageStatus">
                                { error ? esErrorStatus( error ) : t( 'common-error-not-found', '404 Not Found' ) }
                            </p>
                            <p data-testid="es-error-message" className="errorPageError">
                                { error ? esErrorMessage( error ) : t( 'common-error-not-found-message', 'That page does not exist. Use the menu above to get back.' ) }
                            </p>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
